import { ref, watch } from "vue";

/**
 * Composable for staff autocomplete functionality
 * Provides consistent search, selection, and keyboard handling across forms
 *
 * @param {Object} options
 * @param {Ref<Array>} options.staffMembers - Ref or computed of staff members array
 * @param {Function} options.onSelected - Callback when staff is selected (receives staff object)
 * @param {Ref} options.userIdRef - Optional ref to sync userId with (will update when staff changes)
 */
export function useStaffAutocomplete({ staffMembers, onSelected, userIdRef }) {
  const selectedStaff = ref(null);
  const filteredStaff = ref([]);

  // Search handler for AutoComplete
  const searchStaff = (event) => {
    const query = event.query?.toLowerCase() || "";
    const members = staffMembers.value || staffMembers || [];

    if (!query) {
      filteredStaff.value = members;
    } else {
      filteredStaff.value = members.filter((staff) =>
        staff.name.toLowerCase().includes(query)
      );
    }
  };

  // Check if we have a valid object selection (not just typed string)
  const hasValidSelection = () => {
    return selectedStaff.value &&
           typeof selectedStaff.value === "object" &&
           selectedStaff.value.id;
  };

  // Handle Enter key - auto-select single match or call onSelected
  const handleEnter = (event, { advanceOnly = false } = {}) => {
    if (filteredStaff.value.length === 1 && !hasValidSelection()) {
      event.preventDefault();
      selectedStaff.value = filteredStaff.value[0];
      onSelected?.(selectedStaff.value);
    } else if (hasValidSelection()) {
      event.preventDefault();
      if (advanceOnly) {
        // Just advance to next field
        onSelected?.(selectedStaff.value);
      } else {
        // Return true to indicate form should save
        return true;
      }
    }
    return false;
  };

  // Handle Tab key - auto-select single match before tabbing
  const handleTab = (event) => {
    if (filteredStaff.value.length === 1 && !hasValidSelection()) {
      event.preventDefault();
      selectedStaff.value = filteredStaff.value[0];
      onSelected?.(selectedStaff.value);
    }
  };

  // Sync selectedStaff with userIdRef if provided
  if (userIdRef) {
    watch(selectedStaff, (newStaff) => {
      userIdRef.value = newStaff ? newStaff.id : null;
    });
  }

  // Initialize selectedStaff from userId
  const initFromUserId = (userId, members) => {
    const staffList = members || staffMembers.value || staffMembers || [];
    if (userId) {
      selectedStaff.value = staffList.find((s) => s.id === userId) || null;
    } else {
      selectedStaff.value = null;
    }
  };

  // Reset state
  const reset = () => {
    selectedStaff.value = null;
    filteredStaff.value = [];
  };

  return {
    selectedStaff,
    filteredStaff,
    searchStaff,
    handleEnter,
    handleTab,
    hasValidSelection,
    initFromUserId,
    reset,
  };
}
